import { useCallback, useState } from 'react';
import { useFormikContext } from 'formik';
import { useSnackbar } from 'notistack';

import { graphql } from '@e-socna/data-layer';
import type { Id } from '@e-socna/data-layer';

import { useGraphQLMutation } from '../../../hooks/useGraphQLMutation.hook';
import { userFriendlyErrors } from '../../../utils/back-errors.util';
import type { BackError } from '../../../utils/back-errors.util';
import { MessageList } from '../../../components/MessageList';

const MissionReportInfoStateForm_CodeSuggestion_QueryDocument = graphql(`
  query MissionReportInfoStateForm_CodeSuggestion_Query(
    $missionId: Id!
    $workflowId: Id!
    $templateId: Id
  ) {
    suggestReportInfoCode(
      missionId: $missionId
      workflowId: $workflowId
      templateId: $templateId
    )
  }
`);

interface ReportCodeValues {
  readonly code?: string;
  readonly templateId?: Id | string;
}

export const useReportCodeSuggestionFactory = ({
  missionId,
  workflowId,
}: Readonly<{
  missionId: Id;
  workflowId: Id;
}>) =>
  function useReportCodeSuggestion() {
    const { enqueueSnackbar } = useSnackbar();

    const { values, setFieldValue } = useFormikContext<ReportCodeValues>();

    const [isLoading, setIsLoading] = useState(false);

    const { mutate: getCodeSuggestionQuery } = useGraphQLMutation(
      MissionReportInfoStateForm_CodeSuggestion_QueryDocument,
      {
        onMutate: () => {
          setIsLoading(true);
        },
        onSuccess: (data) => {
          void setFieldValue('code', data.suggestReportInfoCode);
        },
        onError: (error: unknown) => {
          enqueueSnackbar(
            <MessageList messages={userFriendlyErrors(error as BackError)} />,
            {
              variant: 'error',
            },
          );
        },
        onSettled: () => {
          setIsLoading(false);
        },
      },
    );

    const codeSuggestionHandler = useCallback(() => {
      getCodeSuggestionQuery({
        missionId,
        workflowId,
        templateId: values.templateId ? (values.templateId as Id) : undefined,
      });
    }, [getCodeSuggestionQuery, values.templateId]);

    return {
      isLoading,
      codeSuggestionHandler,
    };
  };
